import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import Logo from '../images/aren-logo.png';
import './Sidebar.css';

const navClass = ({ isActive }) => `sidebar-nav-item${isActive ? ' sidebar-nav-item-active' : ''}`;

// Admins (CNOs) see aggregate data and rosters: Overview, Insights, Units,
// Nurses and Admins, never individual report sheets. Charge nurses see
// Overview, Report Sheets and Nurses for their own unit, and Aren App (the
// app as they see it on their own phone). A report sheet that's
// open keeps "Report Sheets" highlighted.
function Nav({ isAdmin }) {
  const location = useLocation();
  const onReportPages = location.pathname.startsWith('/reports/');
  return (
    <nav className="sidebar-nav" aria-label="Navigation">
      <p className="sidebar-nav-label">Navigation</p>
      <NavLink to="/" end className={navClass}>Overview</NavLink>
      {isAdmin ? (
        <>
          <NavLink to="/insights" className={navClass}>Insights</NavLink>
          <NavLink to="/units" className={navClass}>Units</NavLink>
          <NavLink to="/nurses" className={navClass}>Nurses</NavLink>
          <NavLink to="/admins" className={navClass}>Admins</NavLink>
        </>
      ) : (
        <>
          <NavLink
            to="/report-sheets"
            className={({ isActive }) => navClass({ isActive: isActive || onReportPages })}
          >
            Report Sheets
          </NavLink>
          <NavLink to="/nurses" className={navClass}>Nurses</NavLink>
          <NavLink to="/app" className={navClass}>Aren App</NavLink>
        </>
      )}
    </nav>
  );
}

function Sidebar() {
  const { nurse, signOut } = useAuth();
  const navigate = useNavigate();
  const isAdmin = nurse.role === 'admin';
  const displayName = [nurse.firstName, nurse.lastName].filter(Boolean).join(' ') || nurse.email;
  const initials = `${nurse.firstName?.[0] || ''}${nurse.lastName?.[0] || ''}`.toUpperCase() || nurse.email[0].toUpperCase();

  // A deliberate logout starts the next sign-in on the home page, not the
  // page the user was on (that's only for sessions that expired).
  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="sidebar">
      <div className="sidebar-title">
        <div className="sidebar-logo">
          <img src={Logo} alt="Aren" />
        </div>
        <div className="sidebar-title-text">
          <span className="title-name">Aren</span>
          <span className="title-subname">{isAdmin ? 'Admin Console' : 'Nurse Console'}</span>
        </div>
      </div>

      <Nav isAdmin={isAdmin} />

      <NavLink to="/profile" className="sidebar-profile">
        <span className="sidebar-avatar" aria-hidden="true">{initials}</span>
        <span className="profile-text">
          <span className="profile-name">{displayName}</span>
          {(nurse.organizationName || nurse.organization) && (
            <span className="profile-unit profile-org">{nurse.organizationName || nurse.organization}</span>
          )}
          {!isAdmin && nurse.unit && <span className="profile-unit">{nurse.unit.name}</span>}
        </span>
      </NavLink>

      <div className="sidebar-logout">
        <button type="button" className="logout-button" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
